import { useCallback, useMemo, useState } from "react";
import { isLiveVisible, liveFreshness, LIVE_TYPES, LIVE_TTL_HOURS, type LiveType } from "@oshimap/domain";
import { relativeDay } from "@oshimap/utils";
import type { LiveUpdateDto } from "@oshimap/schemas";
import { ep } from "../../lib/api";
import { useQuery } from "../../lib/use-query";
import { Button, Skeleton } from "../../components/ui";
import { useAuthPrompt } from "../auth/useAuthPrompt";
import { AuthSheet } from "../auth/AuthSheet";
import { play, wiggle } from "../../lib/motion";
import { liveDetail } from "./format";
import "./live.css";
import { useVisibleRefresh } from "../../lib/use-refresh";
import { queryKeys } from "@oshimap/query";

const LABEL: Record<LiveType, string> = {
  WAIT_TIME: "대기", SOLD_OUT: "품절", RESTOCK: "재입고", BENEFIT_ENDED: "특전 소진",
  BOOKING_CLOSED: "예약 마감", TEMP_CLOSED: "임시 휴업", NORMAL: "평소와 같음",
};

const TONE: Record<LiveType, "warn" | "stop" | "good" | "plain"> = {
  WAIT_TIME: "warn", SOLD_OUT: "stop", RESTOCK: "good", BENEFIT_ENDED: "stop",
  BOOKING_CLOSED: "stop", TEMP_CLOSED: "stop", NORMAL: "good",
};


/**
 * FR-LIVE. 이 제품이 없애려는 것은 헛걸음이다 —
 * 대기 2시간인 줄 모르고 가는 것, 품절인 줄 모르고 가는 것.
 *
 * 그래서 값보다 **언제 본 정보인지**를 먼저 보여준다 (P4).
 * 오래된 정보는 흐리게 두되 감추지 않는다. 없는 것과 낡은 것은 다르다.
 */
export function LiveSection({ placeId }: { placeId: string }) {
  const list = useQuery(queryKeys.placeLive(placeId), (signal) => ep.listPlaceLive(placeId, { signal }));
  /*
   * 서버가 `meta.refetchSeconds: 60` 을 준다 (실측). 그 말대로 따른다 —
   * 줄 서서 화면을 보고 있는 사람에게 5분 전 대기시간을 계속 보여주면 안 된다.
   * 화면이 숨으면 멈추고, 다시 보이면 기다리지 않고 바로 불러온다.
   */
  useVisibleRefresh(60, list.reload);
  const auth = useAuthPrompt();
  const [busy, setBusy] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visible = useMemo(() => {
    if (list.state.status !== "success") return [];
    /* 서버 만료는 조회 시점에만 반영된다 — 표시 직전에 한 번 더 거른다 (AC-07). */
    return list.state.data.filter((u) => isLiveVisible(u));
  }, [list.state]);

  const confirm = useCallback((id: string) => {
    auth.require("현장 정보를 확인하려면 로그인이 필요해요", () => {
      setBusy(id); setError(null);
      void ep.confirmLive(id, "CONFIRM")
        .then(() => list.reload())
        .catch((e: unknown) => setError(e instanceof Error ? e.message : "확인하지 못했어요."))
        .finally(() => setBusy(null));
    });
  }, [auth, list]);

  return (
    <section className="om-live">
      <div className="om-live__head">
        <h2 className="om-h2">지금 현장</h2>
        <Button variant="ghost" size="sm"
          onClick={(e) => {
            play(e.currentTarget, "mo-pop");
            auth.require("현장 정보를 남기려면 로그인이 필요해요", () => setComposing(true));
          }}>알려주기</Button>
      </div>

      {list.state.status === "loading" || list.state.status === "idle" ? <Skeleton lines={2} />
      : list.state.status === "error" ? (
        /* 왜 안 되는지 말합니다. 1분마다 자동으로 다시 걸린다는 것도 함께 알립니다. */
        <p className="om-note om-note--err" role="status">
          현장 정보를 불러오지 못했어요 — {list.state.error.message} 잠시 후 다시 시도합니다.
        </p>
      )
      : visible.length === 0 ? (
        <p className="om-note">
          아직 올라온 소식이 없어요. 다녀오셨다면 대기 시간이나 품절 여부를 알려주세요.
        </p>
      ) : (
        <ul className="om-live__list">
          {visible.map((u) => {
            const type = (LIVE_TYPES as readonly string[]).includes(u.type) ? (u.type as LiveType) : null;
            const fresh = liveFreshness(u);
            const detail = liveDetail(u);
            return (
              <li key={u.id} className={`om-live__item om-live__item--${type ? TONE[type] : "plain"}`}
                style={{ opacity: 0.55 + fresh * 0.45 }}>
                <div className="om-live__row">
                  <b>{type ? LABEL[type] : u.type}{detail ? ` · ${detail}` : ""}</b>
                  {/* 값보다 시각이 먼저다 — 언제 본 정보인지가 값의 뜻을 정한다. */}
                  <time className="num" dateTime={u.observedAt}>{relativeDay(u.observedAt)}</time>
                </div>
                <div className="om-live__row om-live__row--sub">
                  <span className="om-note">
                    {u.confirmedCount > 0 ? `${u.confirmedCount}명이 확인` : "아직 확인한 사람 없음"}
                    {fresh < 0.25 ? " · 오래된 정보예요" : ""}
                  </span>
                  <Button variant="ghost" size="sm" disabled={busy === u.id}
                    onClick={() => confirm(u.id)}>맞아요</Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {error ? <p className="om-note om-note--err" role="alert">{error}</p> : null}
      {composing ? (
        <LiveComposer placeId={placeId}
          onClose={() => setComposing(false)}
          onDone={() => { setComposing(false); list.reload(); }} />
      ) : null}
      {auth.reason ? <AuthSheet reason={auth.reason} onClose={auth.close} /> : null}
    </section>
  );
}

/** 현장에서 한 손으로 쓴다. 고를 것을 적게 두고, 대기시간만 숫자를 받는다. */
function LiveComposer({ placeId, onClose, onDone }: {
  placeId: string; onClose: () => void; onDone: () => void;
}) {
  const [type, setType] = useState<LiveType>("WAIT_TIME");
  const [minutes, setMinutes] = useState(30);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await ep.createLive({
        placeId, type,
        value: type === "WAIT_TIME" ? { minutes } : note.trim() ? { note: note.trim() } : {},
      });
      onDone();
    } catch (e) {
      /* 20회/분 제한이 있다 (실측). 막혔다는 사실을 사람 말로 알린다. */
      setError(e instanceof Error ? e.message : "남기지 못했어요.");
      setBusy(false);
    }
  };

  return (
    <div className="om-livecompose">
      <div className="om-live__types" role="radiogroup" aria-label="현장 정보 유형">
        {LIVE_TYPES.map((t) => (
          <button key={t} type="button" role="radio" aria-checked={type === t}
            className="om-chip"
            onClick={(e) => { play(e.currentTarget, "mo-pop"); setType(t); }}>
            {LABEL[t]}
          </button>
        ))}
      </div>

      {type === "WAIT_TIME" ? (
        <label className="om-field">
          <span>얼마나 기다리셨나요?</span>
          <div className="om-live__minutes">
            {[0, 10, 30, 60, 120].map((m) => (
              <button key={m} type="button" className="om-chip" aria-pressed={minutes === m}
                onClick={() => setMinutes(m)}>{m === 0 ? "바로 입장" : `${m}분`}</button>
            ))}
          </div>
        </label>
      ) : (
        <label className="om-field">
          <span>덧붙일 말 (선택)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={160}
            placeholder="예: 한정판만 품절" />
        </label>
      )}

      {/*
        * 유효 시간을 문장에 직접 적으면 표가 둘이 됩니다. 서버가 TTL 을 바꾸면
        * 이 문장만 옛말이 되고, 사람은 자기 글이 언제 내려가는지 틀리게 압니다.
        * `LIVE_TTL_HOURS` 는 backend 의 `DEFAULT_TTL` 과 같은 값이며, 그것을
        * 테스트가 확인합니다.
        */}
      <p className="om-note">
        올린 정보는 {LIVE_TTL_HOURS[type]}시간 뒤 자동으로 내려갑니다. 현장은 계속 바뀌니까요.
      </p>

      {error ? <p className="om-note om-note--err" role="alert"
        ref={(el) => { if (el) wiggle(el); }}>{error}</p> : null}

      <div className="om-visit__actions">
        <Button variant="ghost" onClick={onClose} disabled={busy}>취소</Button>
        <Button variant="primary" style={{ flex: 1 }} disabled={busy}
          onClick={() => void submit()}>{busy ? "올리는 중…" : "올리기"}</Button>
      </div>
    </div>
  );
}
